import { useState, useRef, useEffect, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";
import { useRealtimeSttStore } from "store/realtimeSttStore";
import { useNavigate } from "react-router-dom";

function uuidToBytes(uuid: string): Uint8Array {
  const bytes: number[] = [];
  uuid.replace(/[a-fA-F0-9]{2}/g, (hex) => {
    bytes.push(parseInt(hex, 16));
    return "";
  });
  return new Uint8Array(bytes) as Uint8Array;
}

const WEBSOCKET_URL = import.meta.env.VITE_WEBSOCKET_URL || "";
const DEBUG_MODE = process.env.NODE_ENV === "development";

// Helper function to validate audio data
const validateAudioData = (data: Float32Array): boolean => {
  if (!data || data.length === 0) {
    if (DEBUG_MODE) console.warn("[DEBUG] 빈 오디오 데이터");
    return false;
  }

  if (data.length > 1600) {
    if (DEBUG_MODE)
      console.warn(`[DEBUG] 오디오 데이터가 너무 큼: ${data.length} > 1600`);
    return false;
  }

  // NaN이나 무한대 값 확인
  for (let i = 0; i < data.length; i++) {
    if (isNaN(data[i]) || !isFinite(data[i])) {
      if (DEBUG_MODE)
        console.warn(
          `[DEBUG] 잘못된 오디오 샘플 발견: index=${i}, value=${data[i]}`
        );
      return false;
    }
  }

  return true;
};

export const useRealtimeStt = () => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [volume, setVolume] = useState(0);
  const [pauseValue, setPauseValue] = useState(0.1);
  const [endValue, setEndValue] = useState(0.7);
  const {
    addFinalTranscript,
    setCurrentTranscript,
    resetTranscripts,
    setFilePath,
    startRecording: startStoreRecording,
    stopRecording: stopStoreRecording,
    setAudioFile,
    setSummaryAndTopic,
  } = useRealtimeSttStore();

  const audioContextRef = useRef<AudioContext | null>(null);
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const accumulatedAudioRef = useRef<Float32Array>(new Float32Array(0));
  const fullRecordingRef = useRef<Float32Array>(new Float32Array(0));
  const sendIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRecordingRef = useRef(false);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const drawVolume = useCallback(() => {
    if (!analyserRef.current) return;
    const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
    analyserRef.current.getByteTimeDomainData(dataArray);

    let sum = 0;
    for (const amplitude of dataArray) {
      sum += Math.abs(amplitude - 128);
    }
    const average = sum / dataArray.length;
    const normalizedVolume = Math.min(average / 32, 1);
    setVolume(normalizedVolume);

    animationFrameRef.current = requestAnimationFrame(drawVolume);
  }, []);

  const disconnect = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setVolume(0);

    if (audioContextRef.current?.state !== "closed") {
      sourceNodeRef.current?.disconnect();
      scriptProcessorRef.current?.disconnect();
      audioContextRef.current?.close();
    }

    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
      audioStreamRef.current = null;
    }

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }

    setIsRecording(false);
    isRecordingRef.current = false;
    stopStoreRecording();
  }, [stopStoreRecording]);

  const stopRecording = useCallback(() => {
    if (!isRecording) {
      return;
    }

    // 즉시 녹음 상태 종료 및 타이머 정지 (UI 즉시 업데이트)
    setIsRecording(false);
    isRecordingRef.current = false;
    stopStoreRecording();

    // 오디오 스트림 중지 (녹음은 즉시 멈춤)
    if (audioStreamRef.current) {
      audioStreamRef.current.getTracks().forEach((track) => track.stop());
    }

    // 오디오 처리 중지
    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    setVolume(0);

    // 오디오 파일 저장
    if (fullRecordingRef.current.length > 0) {
      const blob = new Blob([encodeWAV(fullRecordingRef.current)], {
        type: "audio/wav",
      });
      const file = new File([blob], "realtime_recording.wav", {
        type: "audio/wav",
      });
      setAudioFile(file);
    }

    // WebSocket은 유지하여 서버 응답(file_path, summary, topic)을 받을 수 있도록 함
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send("close");
    } else {
      // WebSocket이 열려있지 않으면 완전히 disconnect
      disconnect();
    }
  }, [isRecording, disconnect, setAudioFile, stopStoreRecording]);

  const startRecording = useCallback(async () => {
    if (isRecording) {
      return;
    }

    const projectId = "audion_rt";

    resetTranscripts();
    setAudioFile(null);
    fullRecordingRef.current = new Float32Array(0);

    try {
      if (navigator.permissions) {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });
        if (permissionStatus.state === "denied") {
          alert(
            "마이크 권한이 차단되었습니다. 브라우저 설정에서 마이크 접근을 허용해주세요."
          );
          return;
        }
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      audioStreamRef.current = stream;

      console.log(
        `[DEBUG] 마이크 스트림 획득: ${stream.getTracks().length}개 트랙`
      );
      stream.getTracks().forEach((track, index) => {
        console.log(
          `[DEBUG] 트랙 ${index}: kind=${track.kind}, enabled=${track.enabled}, readyState=${track.readyState}`
        );
      });

      const audioContext = new (window.AudioContext ||
        (window as any).webkitAudioContext)({
        sampleRate: 16000,
        latencyHint: "interactive",
      });
      audioContextRef.current = audioContext;

      console.log(
        `[DEBUG] AudioContext 생성: sampleRate=${audioContext.sampleRate}, state=${audioContext.state}`
      );

      // AudioContext 상태 복구
      if (audioContext.state === "suspended") {
        try {
          await audioContext.resume();
          console.log("[DEBUG] AudioContext resumed from suspended state");
        } catch (error) {
          console.error("[DEBUG] AudioContext resume 실패:", error);
          alert(
            "오디오 시스템을 초기화할 수 없습니다. 페이지를 새로고침해주세요."
          );
          return;
        }
      } else if (audioContext.state === "closed") {
        console.error("[DEBUG] AudioContext가 이미 닫혀있음");
        alert("오디오 시스템 오류가 발생했습니다. 페이지를 새로고침해주세요.");
        return;
      }

      console.log(
        `[DEBUG] startRecording_17: 웹소켓 연결 시도: ${WEBSOCKET_URL}`
      );
      if (!WEBSOCKET_URL) {
        alert("웹소켓 URL이 설정되지 않았습니다. .env 파일을 확인해주세요.");
        console.error("[DEBUG] startRecording_ERROR: WEBSOCKET_URL is empty.");
        return;
      }
      const socket = new WebSocket(WEBSOCKET_URL);
      socketRef.current = socket;
      const sessionHeader = uuidToBytes(uuidv4().replace(/-/g, ""));

      console.log(`[DEBUG] WebSocket 생성 완료, URL: ${WEBSOCKET_URL}`);
      console.log(
        `[DEBUG] WebSocket 상태: ${socket.readyState} (0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED)`
      );

      socket.onopen = () => {
        console.log(`[DEBUG] WebSocket 연결 성공! 상태: ${socket.readyState}`);
        console.log(
          `[DEBUG] pause=${pauseValue.toFixed(1)}, end=${endValue.toFixed(
            1
          )}, projectId=${projectId}`
        );

        socket.send(
          `pause=${pauseValue.toFixed(1)},end=${endValue.toFixed(
            1
          )},projectId=${projectId}`
        );
        console.log(
          `[DEBUG] 초기 파라미터 전송 완료: pause=${pauseValue.toFixed(
            1
          )}, end=${endValue.toFixed(1)}, projectId=${projectId}`
        );
        console.log("startStoreRecording: ", pauseValue, endValue, projectId);
        startStoreRecording();
        setIsRecording(true);
        isRecordingRef.current = true;

        const source = audioContext.createMediaStreamSource(stream);
        sourceNodeRef.current = source;

        const analyser = audioContext.createAnalyser();
        analyserRef.current = analyser;
        analyser.fftSize = 2048;

        const scriptProcessor = audioContext.createScriptProcessor(1024, 1, 1);
        scriptProcessorRef.current = scriptProcessor;

        console.log(
          `[DEBUG] ScriptProcessor 생성 완료: bufferSize=1024 (64ms)`
        );

        scriptProcessor.onaudioprocess = (e) => {
          if (DEBUG_MODE) {
            console.log(
              `[DEBUG] ScriptProcessor 호출됨! 입력데이터 길이: ${
                e.inputBuffer.getChannelData(0).length
              }`
            );
            console.log(
              `[DEBUG] isRecordingRef.current: ${
                isRecordingRef.current
              }, audioContextRef.current: ${!!audioContextRef.current}`
            );
          }

          if (!isRecordingRef.current || !audioContextRef.current) {
            if (DEBUG_MODE) {
              console.log(
                `[DEBUG] 조건문에서 걸림: isRecordingRef.current=${
                  isRecordingRef.current
                }, audioContextRef.current=${!!audioContextRef.current}`
              );
            }
            return;
          }

          const inputData = e.inputBuffer.getChannelData(0);
          const outputData = e.outputBuffer.getChannelData(0);

          // 출력 데이터를 무음으로 설정 (에코 방지)
          outputData.fill(0);

          let resampledData = inputData;

          const sourceSampleRate = audioContextRef.current.sampleRate;
          const targetSampleRate = 16000;

          if (sourceSampleRate > targetSampleRate) {
            const ratio = sourceSampleRate / targetSampleRate;
            const newLength = Math.floor(inputData.length / ratio);
            resampledData = new Float32Array(newLength);
            let offsetResult = 0;
            let offsetBuffer = 0;
            while (offsetResult < newLength) {
              const nextOffsetBuffer = Math.round((offsetResult + 1) * ratio);
              let accum = 0,
                count = 0;
              for (
                let i = offsetBuffer;
                i < nextOffsetBuffer && i < inputData.length;
                i++
              ) {
                accum += inputData[i];
                count++;
              }
              resampledData[offsetResult] = accum / count;
              offsetResult++;
              offsetBuffer = nextOffsetBuffer;
            }
          }

          if (resampledData.length > 0) {
            // 데이터 검증
            if (!validateAudioData(resampledData)) {
              if (DEBUG_MODE)
                console.warn("[DEBUG] 오디오 데이터 검증 실패, 처리 건너뜀");
              return;
            }

            // 누적 버퍼에 추가
            const newBuffer = new Float32Array(
              accumulatedAudioRef.current.length + resampledData.length
            );
            newBuffer.set(accumulatedAudioRef.current);
            newBuffer.set(resampledData, accumulatedAudioRef.current.length);
            accumulatedAudioRef.current = newBuffer;

            // 1600 샘플이 모이면 전송
            const SAMPLES_PER_100MS = 1600;
            if (
              accumulatedAudioRef.current.length >= SAMPLES_PER_100MS &&
              socket.readyState === WebSocket.OPEN
            ) {
              const chunkToSend = accumulatedAudioRef.current.slice(
                0,
                SAMPLES_PER_100MS
              );

              // 전체 녹음 버퍼에 추가
              const newFullBuffer = new Float32Array(
                fullRecordingRef.current.length + chunkToSend.length
              );
              newFullBuffer.set(fullRecordingRef.current);
              newFullBuffer.set(chunkToSend, fullRecordingRef.current.length);
              fullRecordingRef.current = newFullBuffer;

              // 전송한 데이터 제거
              accumulatedAudioRef.current =
                accumulatedAudioRef.current.slice(SAMPLES_PER_100MS);

              const pcmData = new Int16Array(chunkToSend.length);
              for (let i = 0; i < chunkToSend.length; i++) {
                let s = Math.max(-1, Math.min(1, chunkToSend[i]));
                pcmData[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
              }

              const dataToSend = new Blob([sessionHeader, pcmData] as BlobPart[]);
              if (DEBUG_MODE) {
                console.log(
                  `[DEBUG] WebSocket 데이터 전송: ${dataToSend.size} bytes (오디오: ${pcmData.buffer.byteLength} bytes, 헤더: ${sessionHeader.byteLength} bytes)`
                );
              }
              socket.send(dataToSend);
            }
          } else {
            console.log(
              `[DEBUG] resampledData가 비어있음: inputData.length=${inputData.length}, sourceSampleRate=${sourceSampleRate}, targetSampleRate=${targetSampleRate}`
            );
          }
        };

        source.connect(analyser);
        analyser.connect(scriptProcessor);
        scriptProcessor.connect(audioContext.destination);

        console.log(
          `[DEBUG] 오디오 체인 연결 완료: source → analyser → scriptProcessor → destination`
        );
        console.log(
          `[DEBUG] AudioContext 상태: ${audioContext.state}, sampleRate: ${audioContext.sampleRate}`
        );

        if (audioContext.state !== "running") {
          console.warn(
            "Audio context is not in a running state, audio might not be processed."
          );
        }

        animationFrameRef.current = requestAnimationFrame(drawVolume);

        console.log(`[DEBUG] 녹음 시작 완료!`);
      };

      socket.onmessage = (event) => {
        try {
          const result = JSON.parse(event.data);

          if (result.file_path) {
            setFilePath(result.file_path);
            disconnect();
            return;
          }

          console.log("[DEBUG] result: ", result);

          const { text, status, refined_text, original_text, summary, topic, final } = result;

          console.log("[DEBUG] text: ", text);
          console.log("[DEBUG] status: ", status);
          console.log("[DEBUG] refined_text: ", refined_text);
          console.log("[DEBUG] original_text: ", original_text);
          console.log("[DEBUG] summary: ", summary);
          console.log("[DEBUG] topic: ", topic);
          console.log("[DEBUG] final: ", final);

          // final이 true이고 summary와 topic이 있는 경우 저장
          if (final === true && (summary || topic)) {
            setSummaryAndTopic(summary || null, topic || null);
          }

          // status가 'refined'인 경우 refined_text 사용, 없으면 text 사용
          const displayText = (status === 'refined' && refined_text) 
            ? refined_text 
            : text;

          if (displayText) {
            if (status === 3 || status === 'refined') {
              addFinalTranscript(
                displayText, 
                status === 'refined',
                original_text || undefined
              );
            } else {
              setCurrentTranscript(displayText);
            }
          }
        } catch (error) {
          console.error("[WebSocket] Error parsing server response:", error);
        }
      };

      socket.onerror = (error) => {
        console.error("[DEBUG] WebSocket 연결 오류:", error);
        console.error("[DEBUG] WebSocket URL:", WEBSOCKET_URL);
        console.error("[DEBUG] WebSocket 상태:", socket.readyState);

        // 더 구체적인 에러 메시지
        let errorMessage = "실시간 서버에 연결할 수 없습니다.";
        if (socket.readyState === WebSocket.CONNECTING) {
          errorMessage =
            "서버 연결 중 시간이 초과되었습니다. 네트워크 상태를 확인해주세요.";
        } else if (socket.readyState === WebSocket.CLOSED) {
          errorMessage = "서버와의 연결이 끊어졌습니다. 다시 시도해주세요.";
        }

        alert(errorMessage);
        disconnect();
      };

      socket.onclose = (event) => {
        console.log(
          `[DEBUG] WebSocket 연결 종료. Code: ${event.code}, Reason: ${event.reason}, Was Clean: ${event.wasClean}`
        );

        // 서버에서 의도적으로 연결을 끊은 경우
        if (event.code === 1000) {
          console.log("[DEBUG] 서버에서 정상적으로 연결 종료");
        } else if (event.code === 1011) {
          console.error("[DEBUG] 서버에서 오류로 인해 연결 종료");
          alert("서버에서 오류가 발생했습니다. 잠시 후 다시 시도해주세요.");
        } else if (!event.wasClean) {
          console.error("[DEBUG] 비정상적인 연결 종료");
          alert("네트워크 연결이 불안정합니다. 인터넷 연결을 확인해주세요.");
        }

        disconnect();
      };
    } catch (error) {
      console.error("Could not start recording:", error);
      if (error instanceof DOMException && error.name === "NotAllowedError") {
        alert(
          "마이크 사용 권한이 거부되었습니다. 페이지를 새로고침하고 다시 시도해주세요."
        );
      } else {
        alert("마이크에 접근할 수 없습니다. 마이크 장치를 확인해주세요.");
      }
    }
  }, [
    isRecording,
    resetTranscripts,
    drawVolume,
    disconnect,
    addFinalTranscript,
    setCurrentTranscript,
    setAudioFile,
    startStoreRecording,
    setFilePath,
    navigate,
    pauseValue,
    endValue,
    setSummaryAndTopic,
  ]);

  useEffect(() => {
    return () => {
      console.log("[DEBUG] useRealtimeStt cleanup 시작");

      // 애니메이션 프레임 정리
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
        console.log("[DEBUG] 애니메이션 프레임 정리 완료");
      }

      // 인터벌 정리
      if (sendIntervalRef.current) {
        clearInterval(sendIntervalRef.current);
        sendIntervalRef.current = null;
        console.log("[DEBUG] 인터벌 정리 완료");
      }

      // 오디오 컨텍스트 정리
      if (audioContextRef.current?.state !== "closed") {
        try {
          sourceNodeRef.current?.disconnect();
          scriptProcessorRef.current?.disconnect();
          analyserRef.current?.disconnect();
          audioContextRef.current?.close();
          console.log("[DEBUG] 오디오 컨텍스트 정리 완료");
        } catch (error) {
          console.error("[DEBUG] 오디오 컨텍스트 정리 중 오류:", error);
        }
      }

      // 마이크 스트림 정리
      if (audioStreamRef.current) {
        try {
          audioStreamRef.current.getTracks().forEach((track) => {
            track.stop();
            console.log(`[DEBUG] 마이크 트랙 정지: ${track.kind}`);
          });
          audioStreamRef.current = null;
          console.log("[DEBUG] 마이크 스트림 정리 완료");
        } catch (error) {
          console.error("[DEBUG] 마이크 스트림 정리 중 오류:", error);
        }
      }

      // WebSocket 정리
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        try {
          socketRef.current.close();
          console.log("[DEBUG] WebSocket 정리 완료");
        } catch (error) {
          console.error("[DEBUG] WebSocket 정리 중 오류:", error);
        }
      }

      // 상태 초기화
      setIsRecording(false);
      isRecordingRef.current = false;
      setVolume(0);

      // 버퍼 정리
      accumulatedAudioRef.current = new Float32Array(0);
      fullRecordingRef.current = new Float32Array(0);

      console.log("[DEBUG] useRealtimeStt cleanup 완료");
    };
  }, []);

  return {
    isRecording,
    volume,
    startRecording,
    stopRecording,
    pauseValue,
    setPauseValue,
    endValue,
    setEndValue,
    analyser: analyserRef.current,
    fullRecordingRef,
  };
};

function encodeWAV(samples: Float32Array): ArrayBuffer {
  const sampleRate = 16000;
  const numChannels = 1;
  const bitsPerSample = 16;
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  writeString(view, 0, "RIFF");
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(view, 8, "WAVE");
  writeString(view, 12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, bitsPerSample, true);
  writeString(view, 36, "data");
  view.setUint32(40, samples.length * 2, true);
  floatTo16BitPCM(view, 44, samples);
  return buffer;
}

function writeString(view: DataView, offset: number, string: string) {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function floatTo16BitPCM(
  output: DataView,
  offset: number,
  input: Float32Array
) {
  for (let i = 0; i < input.length; i++, offset += 2) {
    const s = Math.max(-1, Math.min(1, input[i]));
    output.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
  }
}
